// client/src/pages/secure/SeoSettingsPage.js — Administration → Search & Social Metadata.
//
// The managing broker's screen for what Google and link previews show: per-page title and
// description templates with variables, the share image, a Google-result preview and a
// social-card preview with length warnings, the weekly/monthly check (its report, Run now),
// the city landing pages, and AI suggestions that only go live once approved here.
// Everything is read from and saved to /api/seo (routes/seo.js).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import PageHeader from '../../components/shared/PageHeader';

const card = 'rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:p-6';
const input = 'mt-1 block w-full rounded-md border border-slate-400 bg-white px-3 py-2 text-slate-900 focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-600';
const btn = 'rounded-md px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:opacity-50';
const SEV = { problem: 'bg-red-50 text-red-900 border-red-200', warning: 'bg-amber-50 text-amber-900 border-amber-200', info: 'bg-slate-50 text-slate-800 border-slate-200' };
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

const Counter = ({ text, min, max }) => {
    const n = String(text || '').length;
    const tone = n > max ? 'text-red-700' : n < min ? 'text-amber-700' : 'text-green-700';
    const note = n > max ? 'too long — will be cut off' : n < min ? 'short' : 'good length';
    return <span className={`text-xs font-medium ${tone}`}>{n} / {max} characters ({note})</span>;
};

/** How the page would look as a Google result. */
const GooglePreview = ({ p, limits }) => {
    const cut = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s);
    let host = '';
    try { host = new URL(p.canonical).host; } catch { host = ''; }
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3" aria-label="Google result preview">
            <p className="text-xs text-slate-600">{host} › {(p.canonical || '').replace(/^https?:\/\/[^/]+\/?/, '').split('/').filter(Boolean).join(' › ')}</p>
            <p className="text-lg leading-snug text-[#1a0dab]">{cut(p.title, limits.title.max + 5)}</p>
            <p className="text-sm text-slate-700">{cut(p.description, limits.description.max)}</p>
            {p.robots && <p className="mt-1 text-xs font-semibold text-amber-800">Not indexed ({p.robots})</p>}
        </div>
    );
};

/** How the page would look shared on Facebook / LinkedIn / iMessage. */
const SocialPreview = ({ p }) => {
    let host = '';
    try { host = new URL(p.canonical).host; } catch { host = ''; }
    return (
        <div className="overflow-hidden rounded-md border border-slate-300 bg-slate-50" aria-label="Social card preview">
            {p.image
                ? <img src={p.image} alt="" className="h-40 w-full object-cover" />
                : <div className="flex h-24 items-center justify-center bg-slate-200 text-xs text-slate-600">No share image — upload one below</div>}
            <div className="p-3">
                <p className="text-xs uppercase text-slate-500">{host}</p>
                <p className="font-semibold text-slate-900">{p.title}</p>
                <p className="line-clamp-2 text-sm text-slate-700">{p.description}</p>
            </div>
        </div>
    );
};

const RouteEditor = ({ row, limits, variables, api, onChange, aiAvailable, onSuggested }) => {
    const [title, setTitle] = useState(row.template.title);
    const [description, setDescription] = useState(row.template.description);
    const [preview, setPreview] = useState(row);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const first = useRef(true);

    useEffect(() => {
        if (first.current) { first.current = false; return undefined; }
        onChange(row.id, { title, description });
        const t = setTimeout(async () => {
            try { setPreview(await api.post('/api/seo/preview', { route_id: row.id, title, description })); }
            catch (e) { setMsg(e.message); }
        }, 400);
        return () => clearTimeout(t);
    }, [title, description, row.id, api, onChange]);

    const reset = () => { setTitle(row.default_template.title); setDescription(row.default_template.description); };
    const suggest = async () => {
        setBusy(true); setMsg('');
        try { const r = await api.post('/api/seo/suggestions', { target_type: 'route', target_key: row.id }); setMsg(r.message); onSuggested(); }
        catch (e) { setMsg(e.message); }
        finally { setBusy(false); }
    };
    const insert = (setter, v) => setter((s) => `${s}${s.endsWith(' ') || !s ? '' : ' '}{${v}}`);

    return (
        <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3">
                <label className="block text-sm font-semibold text-slate-800">Title template
                    <input className={input} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
                </label>
                <Counter text={preview.preview.title} min={limits.title.min} max={limits.title.max} />
                <label className="block text-sm font-semibold text-slate-800">Description template
                    <textarea className={input} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={400} />
                </label>
                <Counter text={preview.preview.description} min={limits.description.min} max={limits.description.max} />
                <div className="flex flex-wrap gap-1" aria-label="Insert a variable">
                    {Object.entries(variables).map(([k, help]) => (
                        <button key={k} type="button" title={help} onClick={() => insert(setDescription, k)}
                            className="rounded border border-slate-300 bg-slate-50 px-2 py-0.5 font-mono text-xs text-slate-800 hover:bg-slate-100">{`{${k}}`}</button>
                    ))}
                </div>
                <p className="text-xs text-slate-600">Click a variable to add it to the description. Plural: <code>{'{count|space|spaces}'}</code>.</p>
                {preview.unknown_variables && preview.unknown_variables.length > 0 && (
                    <p className="text-sm text-red-700">Unknown variable: {preview.unknown_variables.map((u) => `{${u}}`).join(', ')}</p>
                )}
                {(preview.findings || []).length > 0 && (
                    <ul className="space-y-1 text-sm text-amber-800">{preview.findings.map((f) => <li key={f.code}>⚠ {f.message}</li>)}</ul>
                )}
                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={reset} className={`${btn} border border-slate-300 text-slate-800`}>Use the default</button>
                    {!row.dynamic && aiAvailable && (
                        <button type="button" onClick={suggest} disabled={busy} className={`${btn} border border-indigo-300 text-indigo-800`}>
                            {busy ? 'Asking…' : 'Suggest a description (AI)'}
                        </button>
                    )}
                </div>
                {msg && <p className="text-sm text-slate-700">{msg}</p>}
            </div>
            <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-800">Google result</p>
                <GooglePreview p={preview.preview} limits={limits} />
                <p className="text-sm font-semibold text-slate-800">Shared link</p>
                <SocialPreview p={preview.preview} />
            </div>
        </div>
    );
};

const SeoSettingsPage = () => {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [templates, setTemplates] = useState({});
    const [open, setOpen] = useState('home');
    const [notice, setNotice] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState('');
    const [edits, setEdits] = useState({});

    const load = useCallback(async () => {
        try {
            const d = await api.get('/api/seo');
            setData(d);
            setForm({ ...d.settings, service_area_text: (d.settings.service_area_cities || []).join('\n') });
            setTemplates(Object.fromEntries(d.routes.map((r) => [r.id, r.template])));
            setError('');
        } catch (e) { setError(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const onTemplateChange = useCallback((id, t) => setTemplates((x) => ({ ...x, [id]: t })), []);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

    const save = async () => {
        setBusy('save'); setNotice(''); setError('');
        // Only templates that differ from the default are stored, so a later improvement to a
        // default reaches every page nobody customised.
        const custom = {};
        for (const r of data.routes) {
            const t = templates[r.id] || {};
            const title = t.title !== r.default_template.title ? t.title : '';
            const description = t.description !== r.default_template.description ? t.description : '';
            if (title || description) custom[r.id] = { title, description };
        }
        try {
            const r = await api.put('/api/seo/settings', {
                run_frequency: form.run_frequency, templates: custom, og_image_alt: form.og_image_alt || '',
                twitter_handle: form.twitter_handle || '', service_area_cities: form.service_area_text || '',
                landing_pages_enabled: !!form.landing_pages_enabled, landing_min_listings: Number(form.landing_min_listings) || 2,
                check_live_pages: !!form.check_live_pages, indexnow_enabled: !!form.indexnow_enabled, ai_suggestions_enabled: !!form.ai_suggestions_enabled,
            });
            setNotice(r.message); await load();
        } catch (e) { setError(e.message); }
        finally { setBusy(''); }
    };

    const run = async (dry) => {
        setBusy(dry ? 'dry' : 'run'); setNotice(''); setError('');
        try {
            const r = await api.post('/api/seo/run', { dry_run: dry });
            setNotice(`${dry ? 'Dry run (nothing saved, live pages not loaded): ' : ''}${r.message}`);
            if (dry && r.report) setData((d) => ({ ...d, report: r.report }));
            if (!dry) {
                // A real run continues on the server; wait for it, then reload.
                for (let i = 0; i < 60; i += 1) {
                    await new Promise((ok) => setTimeout(ok, 5000));
                    const st = await api.get('/api/seo/run-status');
                    if (!st.running) break;
                }
                await load();
                setNotice('The check finished: see Last check below.');
            }
        } catch (e) { setError(e.message); }
        finally { setBusy(''); }
    };

    const uploadImage = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('image', file);
        if (form.og_image_alt) fd.append('alt', form.og_image_alt);
        setBusy('img');
        try { const r = await api.post('/api/seo/og-image', fd, true); setNotice(r.message); await load(); }
        catch (err) { setError(err.message); }
        finally { setBusy(''); e.target.value = ''; }
    };
    const removeImage = async () => {
        try { const r = await api.delete('/api/seo/og-image'); setNotice(r.message); await load(); } catch (e) { setError(e.message); }
    };

    const decide = async (s, action) => {
        setBusy(`s${s.suggestion_id}`);
        try {
            const r = await api.post(`/api/seo/suggestions/${s.suggestion_id}/${action}`, action === 'approve' ? { text: edits[s.suggestion_id] ?? s.suggested_text } : {});
            setNotice(r.message); await load();
        } catch (e) { setError(e.message); }
        finally { setBusy(''); }
    };
    const suggestListing = async (key) => {
        setBusy(`l${key}`);
        try { const r = await api.post('/api/seo/suggestions', { target_type: 'listing', target_key: key }); setNotice(r.message); await load(); }
        catch (e) { setError(e.message); }
        finally { setBusy(''); }
    };
    const dropOverride = async (key) => {
        try { const r = await api.delete(`/api/seo/overrides/${encodeURIComponent(key)}`); setNotice(r.message); await load(); } catch (e) { setError(e.message); }
    };

    if (!data || !form) return <div className="p-8">{error ? <span className="text-red-700">{error}</span> : 'Loading…'}</div>;
    const report = data.report || { issues: [], fixed: [] };
    const problems = report.issues.filter((i) => i.severity === 'problem');
    const warnings = report.issues.filter((i) => i.severity !== 'problem');

    return (
        <div className="space-y-6">
            <PageHeader title="Search & social metadata" className="!mb-0"
                description={`What Google, Bing and shared links show for ${data.site.base} (${data.site.state}). Last check: ${fmt(form.last_run_at)}.`}
                actions={<button onClick={() => run(false)} disabled={!!busy} className={`${btn} bg-slate-900 text-white hover:bg-slate-900`}>{busy === 'run' ? 'Running…' : 'Run the check now'}</button>}
                secondary={<button onClick={() => run(true)} disabled={!!busy} className={`${btn} border border-slate-300 text-slate-800`}>{busy === 'dry' ? 'Checking…' : 'Dry run'}</button>} />
            {!data.available && <p className="rounded bg-amber-50 p-3 text-sm text-amber-900">The database tables are not set up yet (migrations/2026_10_08_seo_automation.sql). Previews work; saving does not.</p>}
            {error && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
            {notice && <p role="status" className="rounded bg-indigo-50 p-3 text-sm text-indigo-900">{notice}</p>}

            {/* The report */}
            <section className={card} aria-labelledby="report-h">
                <h2 id="report-h" className="text-lg font-bold text-slate-900">Last check</h2>
                {!data.report ? <p className="mt-2 text-sm text-slate-600">No check has run yet. It runs in the morning automation when due, or press Run the check now.</p> : (
                    <div className="mt-3 grid gap-4 lg:grid-cols-2">
                        <div>
                            <h3 className="font-semibold text-slate-800">Needs a person ({problems.length} problem{problems.length === 1 ? '' : 's'}, {warnings.length} warning{warnings.length === 1 ? '' : 's'})</h3>
                            <ul className="mt-2 max-h-96 space-y-2 overflow-y-auto">
                                {report.issues.length === 0 && <li className="text-sm text-green-800">Nothing — every page looks right.</li>}
                                {report.issues.map((i, n) => (
                                    <li key={n} className={`rounded border p-2 text-sm ${SEV[i.severity] || SEV.info}`}>
                                        {i.path && <span className="font-mono text-xs">{i.path}</span>}
                                        <p>{i.message}</p>
                                        {i.listing_key && i.code === 'thin_description' && data.ai_available && (
                                            <button onClick={() => suggestListing(i.listing_key)} disabled={!!busy} className="mt-1 text-xs font-semibold text-indigo-800 underline">
                                                {busy === `l${i.listing_key}` ? 'Asking…' : 'Suggest a search description (AI, you approve)'}
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-800">Fixed automatically ({report.fixed.length})</h3>
                            <ul className="mt-2 max-h-96 space-y-1 overflow-y-auto text-sm text-slate-700">
                                {report.fixed.map((f, n) => <li key={n}>✓ {f.path ? <span className="font-mono text-xs">{f.path} </span> : null}{f.message}</li>)}
                            </ul>
                        </div>
                    </div>
                )}
                {data.runs.length > 0 && (
                    <details className="mt-4">
                        <summary className="cursor-pointer text-sm font-semibold text-slate-800">Run history</summary>
                        <ul className="mt-2 space-y-1 text-sm text-slate-700">
                            {data.runs.map((r) => <li key={r.run_id}>{fmt(r.started_at)} ({r.trigger_source}) — {r.outcome || 'running'}: {r.summary}</li>)}
                        </ul>
                    </details>
                )}
                <p className="mt-3 text-xs text-slate-600">Also on <Link to="/dashboard/system-status" className="underline">System Status</Link>.</p>
            </section>

            {/* Suggestions */}
            {data.suggestions.length > 0 && (
                <section className={card} aria-labelledby="sugg-h">
                    <h2 id="sugg-h" className="text-lg font-bold text-slate-900">AI suggestions waiting for you ({data.suggestions.length})</h2>
                    <p className="text-sm text-slate-600">Nothing here is on the site until you approve it. Edit freely; the fair-housing check runs again when you approve.</p>
                    <ul className="mt-3 space-y-4">
                        {data.suggestions.map((s) => {
                            const blocks = (s.lint || []).filter((f) => f.severity === 'block');
                            return (
                                <li key={s.suggestion_id} className="rounded border border-slate-200 p-3">
                                    <p className="text-xs text-slate-600">{s.target_type === 'listing' ? 'Listing' : 'Page'} <span className="font-mono">{s.target_path || s.target_key}</span> — description</p>
                                    {s.current_text && <p className="mt-1 text-sm text-slate-600"><span className="font-semibold">Now:</span> {String(s.current_text).slice(0, 300)}</p>}
                                    <textarea className={input} rows={3} value={edits[s.suggestion_id] ?? s.suggested_text}
                                        onChange={(e) => setEdits((x) => ({ ...x, [s.suggestion_id]: e.target.value }))} aria-label="Suggested description" />
                                    {(s.lint || []).length > 0 && (
                                        <ul className="mt-1 text-sm">
                                            {s.lint.map((f, n) => <li key={n} className={f.severity === 'block' ? 'text-red-800' : 'text-amber-800'}>{f.severity === 'block' ? '✕' : '⚠'} “{f.term}” — {f.why} {f.suggestion}</li>)}
                                        </ul>
                                    )}
                                    <div className="mt-2 flex gap-2">
                                        <button onClick={() => decide(s, 'approve')} disabled={!!busy} className={`${btn} bg-green-700 text-white`}>{blocks.length ? 'Approve edited text' : 'Approve'}</button>
                                        <button onClick={() => decide(s, 'reject')} disabled={!!busy} className={`${btn} border border-slate-300 text-slate-800`}>Reject</button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            {/* Templates */}
            <section className={card} aria-labelledby="tpl-h">
                <h2 id="tpl-h" className="text-lg font-bold text-slate-900">Page titles and descriptions</h2>
                <p className="text-sm text-slate-600">Numbers like {'{count}'} are refreshed by each check. Aim for titles of {data.limits.title.min}–{data.limits.title.max} characters and descriptions of {data.limits.description.min}–{data.limits.description.max}.</p>
                <ul className="mt-3 divide-y divide-slate-200">
                    {data.routes.map((r) => (
                        <li key={r.id} className="py-3">
                            <button type="button" onClick={() => setOpen(open === r.id ? '' : r.id)} aria-expanded={open === r.id}
                                className="flex w-full items-center justify-between gap-2 text-left">
                                <span>
                                    <span className="font-semibold text-slate-900">{r.label}</span>{' '}
                                    <span className="font-mono text-xs text-slate-500">{r.path}</span>
                                    {r.custom && <span className="ml-2 rounded bg-indigo-100 px-1.5 text-xs text-indigo-900">customised</span>}
                                </span>
                                <span className={`text-xs ${r.findings.length ? 'text-amber-800' : 'text-green-800'}`}>{r.findings.length ? `${r.findings.length} warning(s)` : 'OK'}</span>
                            </button>
                            {open === r.id && (
                                <div className="mt-3">
                                    <RouteEditor row={r} limits={data.limits} variables={data.variables} api={api} onChange={onTemplateChange}
                                        aiAvailable={data.ai_available && form.ai_suggestions_enabled} onSuggested={load} />
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
                <button onClick={save} disabled={!!busy} className={`${btn} mt-3 bg-slate-900 text-white hover:bg-slate-900`}>{busy === 'save' ? 'Saving…' : 'Save all settings'}</button>
            </section>

            {/* Share image */}
            <section className={card} aria-labelledby="img-h">
                <h2 id="img-h" className="text-lg font-bold text-slate-900">Share image</h2>
                <p className="text-sm text-slate-600">Shown when a page without its own photo is shared (property pages use the listing's first photo). 1200 × 630 pixels works best; JPEG, PNG or WebP up to 5 MB.</p>
                <div className="mt-3 flex flex-wrap items-start gap-4">
                    {form.og_image_url ? <img src={form.og_image_url} alt={form.og_image_alt || 'Current share image'} className="h-32 rounded border border-slate-200 object-cover" /> : <p className="text-sm text-slate-600">No image yet.</p>}
                    <div className="space-y-2">
                        <label className="block text-sm font-semibold text-slate-800">Describe the image (alt text)
                            <input className={input} value={form.og_image_alt || ''} onChange={set('og_image_alt')} maxLength={250} />
                        </label>
                        <label className="block text-sm font-semibold text-slate-800">Upload a new image
                            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadImage} disabled={busy === 'img'} className="mt-1 block text-sm" />
                        </label>
                        {form.og_image_url && <button onClick={removeImage} className="text-sm text-red-700 underline">Remove the image</button>}
                    </div>
                </div>
            </section>

            {/* Schedule and switches */}
            <section className={card} aria-labelledby="sched-h">
                <h2 id="sched-h" className="text-lg font-bold text-slate-900">The automatic check</h2>
                <fieldset className="mt-2">
                    <legend className="text-sm font-semibold text-slate-800">How often (in the morning automation run)</legend>
                    {['weekly', 'monthly'].map((f) => (
                        <label key={f} className="mr-6 inline-flex items-center gap-2 text-sm">
                            <input type="radio" name="freq" value={f} checked={form.run_frequency === f} onChange={set('run_frequency')} /> {f === 'weekly' ? 'Weekly (recommended)' : 'Monthly'}
                        </label>
                    ))}
                </fieldset>
                <p className="mt-1 text-xs text-slate-600">Each check: rebuilds the sitemap and image sitemap, refreshes the numbers in the titles, fixes what is safe (standard descriptions, photo alt text, duplicate property titles, noindex on thin city pages) and lists the rest above.</p>
                <div className="mt-4 space-y-2 text-sm">
                    <label className="flex items-start gap-2"><input type="checkbox" checked={!!form.check_live_pages} onChange={set('check_live_pages')} className="mt-1" />
                        <span>Load the live pages and check each answers 200 with the right canonical link</span></label>
                    <label className="flex items-start gap-2"><input type="checkbox" checked={!!form.indexnow_enabled} onChange={set('indexnow_enabled')} className="mt-1" />
                        <span>Tell Bing, Yandex and other IndexNow search engines which pages changed (Google no longer accepts pings; it reads the sitemap listed in robots.txt — add the site in Google Search Console once).
                            {form.indexnow_key_url && <> Key file: <a href={form.indexnow_key_url} className="break-all text-indigo-700 underline" target="_blank" rel="noreferrer">{form.indexnow_key_url}</a></>}</span></label>
                    <label className="flex items-start gap-2"><input type="checkbox" checked={!!form.ai_suggestions_enabled} onChange={set('ai_suggestions_enabled')} className="mt-1" disabled={!data.ai_available} />
                        <span>AI suggestions for thin descriptions (up to 3 per check). They wait here for your approval and are fair-housing checked.
                            {!data.ai_available && <> Needs the Anthropic key in <Link to="/dashboard/integrations" className="underline">Integrations</Link>.</>}</span></label>
                    <label className="block font-semibold text-slate-800">X / Twitter handle (optional)
                        <input className={`${input} max-w-xs`} value={form.twitter_handle || ''} onChange={set('twitter_handle')} placeholder="@yourbrokerage" />
                    </label>
                </div>
                <button onClick={save} disabled={!!busy} className={`${btn} mt-4 bg-slate-900 text-white hover:bg-slate-900`}>{busy === 'save' ? 'Saving…' : 'Save all settings'}</button>
            </section>

            {/* Landing pages */}
            <section className={card} aria-labelledby="land-h">
                <h2 id="land-h" className="text-lg font-bold text-slate-900">City landing pages</h2>
                <p className="text-sm text-slate-600">“List your property in …” and “Commercial space for lease in …” pages, made for every city with listings plus the cities below. A page with fewer listings than the minimum is published but hidden from search (noindex) until it fills up.</p>
                <div className="mt-3 grid gap-4 md:grid-cols-2">
                    <div className="space-y-2 text-sm">
                        <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.landing_pages_enabled} onChange={set('landing_pages_enabled')} /> Publish city landing pages</label>
                        <label className="block font-semibold text-slate-800">Minimum listings to be indexed
                            <input type="number" min="1" max="50" className={`${input} max-w-[8rem]`} value={form.landing_min_listings} onChange={set('landing_min_listings')} />
                        </label>
                        <label className="block font-semibold text-slate-800">Service area — one city per line
                            <textarea className={input} rows={6} value={form.service_area_text} onChange={set('service_area_text')} placeholder={'Northbrook\nSkokie\nEvanston'} />
                        </label>
                    </div>
                    <div className="text-sm">
                        {[['list', 'List your property'], ['lease', 'Commercial space for lease']].map(([k, label]) => (
                            <div key={k} className="mb-3">
                                <h3 className="font-semibold text-slate-800">{label} ({data.landing[k].length})</h3>
                                <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto">
                                    {data.landing[k].map((c) => (
                                        <li key={c.slug}><a href={c.path} target="_blank" rel="noreferrer" className="text-indigo-700 underline">{c.city}</a> — {c.count} listing{c.count === 1 ? '' : 's'}{c.thin ? ' · noindex (thin)' : ''}</li>
                                    ))}
                                    {data.landing[k].length === 0 && <li className="text-slate-600">None yet.</li>}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
                <button onClick={save} disabled={!!busy} className={`${btn} mt-2 bg-slate-900 text-white hover:bg-slate-900`}>{busy === 'save' ? 'Saving…' : 'Save all settings'}</button>
            </section>

            {/* Overrides */}
            {data.overrides.length > 0 && (
                <section className={card} aria-labelledby="ov-h">
                    <h2 id="ov-h" className="text-lg font-bold text-slate-900">Page-specific titles and descriptions</h2>
                    <ul className="mt-2 space-y-1 text-sm">
                        {data.overrides.map((o) => (
                            <li key={`${o.target_key}-${o.source}`}>
                                <span className="font-mono text-xs">{o.target_key}</span> — {o.source === 'auto' ? 'automatic' : 'approved'}: {o.title || o.description}
                                {o.source === 'approved' && <button onClick={() => dropOverride(o.target_key)} className="ml-2 text-xs text-red-700 underline">Remove</button>}
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
};

export default SeoSettingsPage;
